import { useCallback, useState } from "react";
import { useFusionAuth } from "@fusionauth/react-sdk";
import { HTTPMethod } from "@fusionauth/typescript-client";

const no_data_response_codes = [202, 204];

type ReasonForDeleteType = string;

export function useFetch<
  ReturnType,
  RequestBodyType = undefined,
  OriginalResponseType = ReturnType,
  DeleteBodyType = RequestBodyType | ReasonForDeleteType,
>() {
  type responseManipulationFunctionType =
    | ((data: OriginalResponseType) => ReturnType)
    | undefined;
  type postResponseActionType =
    | ((data: ReturnType | undefined) => void)
    | undefined;

  type useFetchArgs = {
    path: string;
    body?: RequestBodyType | DeleteBodyType;
    method?: HTTPMethod;
    responseManipulationFunction?: responseManipulationFunctionType;
    postResponseAction?: postResponseActionType;
  };

  const { startLogout } = useFusionAuth();
  const [data, setData] = useState<ReturnType | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [status, setStatus] = useState<number | null>(null);
  const [controller] = useState(new AbortController());

  const makeCall = useCallback(
    async ({
      path,
      body = undefined,
      method = HTTPMethod.GET,
      responseManipulationFunction = undefined,
      postResponseAction = undefined,
    }: useFetchArgs) => {
      setIsLoading(true);
      setStatus(null);
      setError(null);
      try {
        const response = await fetch(path, {
          signal: controller.signal,
          method: method,
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            data: "JSON",
          },
          credentials: "include",
          body: body
            ? typeof body === "string"
              ? body
              : JSON.stringify(body)
            : null,
        });
        if (controller.signal.aborted) return;
        setStatus(response.status);
        let data;
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            void startLogout();
          }
          if (response.status === 404) {
            setError("No data found");
          } else {
            const error = await response.json();
            setError(error);
            // setError(response.body.toString());
          }
        } else if (!no_data_response_codes.includes(response.status)) {
          const contentLength = response.headers.get("Content-Length");
          if (contentLength && parseInt(contentLength) > 0) {
            data = await response.json();
            if (data) {
              if (responseManipulationFunction) {
                data = responseManipulationFunction(data);
              }
              setData(data);
            }
          }
        }
        setIsLoading(false);
        if (postResponseAction) {
          postResponseAction(data);
        }
      } catch (err) {
        console.error(err);
      }

      return () => {
        controller.abort();
      }; // added
    },
    [startLogout, controller],
  );

  const fetchData = useCallback(
    (
      path: string,
      {
        responseManipulationFunction,
        postResponseAction,
      }: Omit<useFetchArgs, "path" | "body" | "method"> = {},
    ) => {
      return makeCall({
        path,
        responseManipulationFunction,
        postResponseAction,
      });
    },
    [makeCall],
  );

  const createData = useCallback(
    ({
      path,
      body,
      method = HTTPMethod.POST,
      responseManipulationFunction,
      postResponseAction,
    }: useFetchArgs) => {
      return makeCall({
        path,
        body,
        method,
        responseManipulationFunction,
        postResponseAction,
      });
    },
    [makeCall],
  );

  const updateData = useCallback(
    ({
      path,
      body,
      method = HTTPMethod.PUT,
      responseManipulationFunction,
      postResponseAction,
    }: useFetchArgs) => {
      return makeCall({
        path,
        body,
        method,
        responseManipulationFunction,
        postResponseAction,
      });
    },
    [makeCall],
  );

  const deleteData = useCallback(
    ({
      path,
      body,
      method = HTTPMethod.DELETE,
      responseManipulationFunction,
      postResponseAction,
    }: useFetchArgs) => {
      return makeCall({
        path,
        body,
        method,
        responseManipulationFunction,
        postResponseAction,
      });
    },
    [makeCall],
  );

  const abortCall = useCallback(
    (reason: string) => {
      return controller.abort(reason);
    },
    [controller],
  );

  const resetData = useCallback(() => {
    setStatus(null);
    setData(undefined);
    setIsLoading(false);
    setError(null);
  }, []);

  return {
    abort: abortCall,
    data,
    error,
    isLoading,
    status,
    fetch: fetchData,
    create: createData,
    update: updateData,
    delete: deleteData,
    reset: resetData,
  };
}
